/**
 * The three honest chips of spec 10 §3, and the sync detail sheet behind them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROMISE THIS COMPONENT KEEPS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **Gate G-18: the green chip may never render while the queue is non-empty.** It is kept
 * not by a condition in this file but by the shape of {@link QueueStatus.state}, which is
 * derived from the queue's own depth before anything else is consulted (`noteQueue.ts`).
 * This component renders whichever of three strings the state names, and has no branch that
 * could reach the green one another way.
 *
 * | State | Chip | Condition |
 * |---|---|---|
 * | Synced | `All Notes Synced ✓` | Queue empty |
 * | Waiting | `3 Waiting To Send` (`1 Waiting To Send`) | Queue non-empty, back-off not spent |
 * | Unreachable | `Cannot Reach Server` | Back-off ceiling reached |
 *
 * **There is no retry button, and adding one is not a small change.** Spec 10 §1: *"a sync
 * button is an admission that sync might not happen on its own, and it invites a UM to
 * believe a note is lost when it is merely queued."* Ruling Q-10a kept one exception — a
 * hidden long-press retry *"for the TD only, for use during venue testing"* — and it is
 * deliberately not built, because `UmShell` turns a TD away before this component renders at
 * all, so the control would be unreachable code wearing the costume of a feature.
 *
 * The count is **notes**, not writes: spec 10 §5 step 3 captures six notes and expects
 * `6 Waiting To Send`, and step 4 corrects a stamp and moves a note between umpires and
 * expects the figure not to move, "because corrections are events on existing notes". Two
 * queued writes about one note are one note waiting.
 */

import { useState } from "react";

import { notesOf, type QueuedWrite } from "../../api/noteQueue.js";
import { useQueueStatus } from "../../app/useQueueStatus.js";

/** `15:42` in the tournament's own timezone — the app renders Tokyo (spec 00 §7.3). */
export function tokyoClock(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
}

/** What one queued write is called in the sheet's per-note list. Plain words, no jargon. */
export function writeLabel(write: QueuedWrite): string {
  if (write.kind === "capture") return "Note";
  if (write.kind === "lock") return "Lock";
  return notesOf(write).length === 1 ? "Release · 1 Note" : `Release · ${String(notesOf(write).length)} Notes`;
}

function writeDetail(write: QueuedWrite): string {
  if (write.kind === "capture") {
    const body = write.note.bodyEn.trim();
    if (body !== "") return body;
    return write.note.tagIds.length > 0 ? "Tagged Observation" : "Observation";
  }
  if (write.kind === "lock") return "Keeping This Note To Yourself";
  return "Sharing With The UM Team";
}

export function SyncChip(): React.JSX.Element {
  const status = useQueueStatus();
  const [open, setOpen] = useState(false);

  const label =
    status.state === "synced"
      ? "All Notes Synced ✓"
      : status.state === "unreachable"
        ? "Cannot Reach Server"
        : `${String(status.depth)} Waiting To Send`;

  const tone =
    status.state === "synced"
      ? "statuspill--good"
      : status.state === "unreachable"
        ? "statuspill--alert"
        : "statuspill--warn";

  return (
    <div className="syncchip">
      <button
        type="button"
        className={`statuspill ${tone} syncchip__btn`}
        aria-expanded={open}
        data-testid="sync-chip"
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span className="sdot" aria-hidden="true" />
        {label}
      </button>

      {/*
       * Q-10c. A failed IndexedDB write is the one way a note could genuinely be lost, so it
       * is said loudly and outside the sheet — a UM who never taps the chip must still see
       * it. The note is still in this session's queue and will still be sent; what it has
       * lost is its survival across a force-quit, and that is what the copy says.
       */}
      {status.storageFailed ? (
        <p className="syncwarn" role="alert">
          This Phone Would Not Store Your Latest Note. It Will Still Send From This Session —
          But Do Not Close The App Until The Chip Goes Green.
        </p>
      ) : null}

      {status.refused.map((entry) => (
        <p key={entry.write.seq} className="syncwarn" role="alert">
          {`Not Sent — ${entry.message}`}
        </p>
      ))}

      {open ? (
        <div className="syncsheet" role="group" aria-label="Sync detail" data-testid="sync-sheet">
          <p className="syncsheet__lead">
            {status.lastSyncAt === null
              ? "Nothing Has Been Sent From This Phone Yet."
              : `Last Sent ${tokyoClock(status.lastSyncAt)}`}
          </p>

          {status.state === "unreachable" ? (
            /* §3's sub-line, verbatim. It is the whole point of the alert chip: the server is
               the thing that is unreachable, and the notes are not the thing that is at risk. */
            <p className="syncsheet__safe">
              {`Your Notes Are Safe On This Phone${
                status.lastSyncAt === null ? "" : ` · Last Sent ${tokyoClock(status.lastSyncAt)}`
              }`}
            </p>
          ) : null}

          <p className="syncsheet__depth num">
            {status.depth === 0
              ? "Nothing Is Waiting"
              : `${String(status.depth)} ${status.depth === 1 ? "Note" : "Notes"} Waiting`}
          </p>

          {status.writes.length > 0 ? (
            <ul className="syncsheet__list">
              {status.writes.map((write) => (
                <li key={write.seq}>
                  <span className="syncsheet__kind">{writeLabel(write)}</span>
                  <span className="syncsheet__what">{writeDetail(write)}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {/*
           * §3's copy, verbatim, and the reason there is no button under it. The sheet's
           * words do the reassurance work that a retry control would otherwise be asked to
           * do — which is the whole of ruling Q-10a.
           */}
          <p className="syncsheet__foot">Everything Sends By Itself. Nothing Here Can Be Lost.</p>
        </div>
      ) : null}
    </div>
  );
}
